import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from "@/components/ui/dialog";
import { AdminDialogHeading } from "@/components/admin/AdminDialogHeading";
import { Img } from "@/components/ui/img";
import { cn } from "@/lib/utils";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import {
  Star,
  CaretUp,
  CaretDown,
  X,
  Plus,
  FloppyDisk,
  CircleNotch,
  MagnifyingGlass,
  UserPlus,
  CheckCircle,
  Warning,
} from "@phosphor-icons/react";

interface FeaturedTherapist {
  id: string;
  userName: string;
  userImage: string | null;
  profileImageUrl: string | null;
  specializations: string | string[];
}

interface FeaturedResponse {
  featured: FeaturedTherapist[];
  therapistIds: string[];
}

interface ApprovedTherapist {
  id: string;
  userName: string;
  userImage: string | null;
  profileImageUrl: string | null;
  specializations: string | string[];
}

function parseSpecs(raw: string | string[]): string[] {
  if (Array.isArray(raw)) return raw;
  try { return JSON.parse(raw); } catch { return []; }
}

export function FeaturedTherapistsSection() {
  const queryClient = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [localIds, setLocalIds] = useState<string[] | null>(null);
  const [localFeatured, setLocalFeatured] = useState<FeaturedTherapist[] | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "featured-therapists"],
    queryFn: async () => {
      const res = await fetch("/api/admin/featured-therapists");
      await throwIfNotOk(res, "Greška pri učitavanju istaknutih terapeuta");
      return res.json() as Promise<FeaturedResponse>;
    },
  });

  // Sync server data to local state when it arrives (only if no local edits)
  // IDs follow the visible (active) list so stale IDs can't fill the slots or
  // shift indexes between the two arrays.
  const therapistIds = localIds ?? data?.featured.map((t) => t.id) ?? [];
  const featured = localFeatured ?? data?.featured ?? [];
  const hasChanges = localIds !== null;

  const saveMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const res = await fetch("/api/admin/featured-therapists", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ therapistIds: ids }),
      });
      await throwIfNotOk(res, "Greška pri spremanju istaknutih terapeuta");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "featured-therapists"],
      });
      setLocalIds(null);
      setLocalFeatured(null);
      toast.success("Istaknuti terapeuti su uspješno ažurirani");
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const moveUp = (index: number) => {
    if (index === 0) return;
    const newIds = [...therapistIds];
    const newFeatured = [...featured];
    [newIds[index - 1], newIds[index]] = [newIds[index], newIds[index - 1]];
    [newFeatured[index - 1], newFeatured[index]] = [newFeatured[index], newFeatured[index - 1]];
    setLocalIds(newIds);
    setLocalFeatured(newFeatured);
  };

  const moveDown = (index: number) => {
    if (index >= therapistIds.length - 1) return;
    const newIds = [...therapistIds];
    const newFeatured = [...featured];
    [newIds[index], newIds[index + 1]] = [newIds[index + 1], newIds[index]];
    [newFeatured[index], newFeatured[index + 1]] = [newFeatured[index + 1], newFeatured[index]];
    setLocalIds(newIds);
    setLocalFeatured(newFeatured);
  };

  const removeTherapist = (index: number) => {
    const newIds = therapistIds.filter((_, i) => i !== index);
    const newFeatured = featured.filter((_, i) => i !== index);
    setLocalIds(newIds);
    setLocalFeatured(newFeatured);
  };

  const addTherapist = (therapist: ApprovedTherapist) => {
    if (therapistIds.length >= 4) return;
    if (therapistIds.includes(therapist.id)) return;
    setLocalIds([...therapistIds, therapist.id]);
    setLocalFeatured([...featured, therapist]);
    setPickerOpen(false);
    setSearchQuery("");
  };

  const handleSave = () => {
    saveMutation.mutate(therapistIds);
  };

  if (isLoading) {
    return (
      <div className="rounded-xl border bg-card p-5 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="size-10 animate-pulse rounded-xl bg-muted" />
          <div className="flex-1 space-y-2">
            <div className="h-5 w-48 animate-pulse rounded bg-muted" />
            <div className="h-3.5 w-80 animate-pulse rounded bg-muted/60" />
            <div className="mt-4 space-y-3">
              {[...Array(2)].map((_, i) => (
                <div
                  key={i}
                  className="h-16 animate-pulse rounded-lg bg-muted/40"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border bg-card p-4 shadow-sm sm:p-5">
        <div className="flex items-start gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/8">
            <Star className="size-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <h3
              className="text-base font-semibold text-foreground"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              Istaknuti terapeuti
            </h3>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Odaberite do 4 terapeuta koji će biti prikazani na početnoj
              stranici
            </p>

            {/* Featured therapist cards */}
            <div className="mt-4 space-y-2">
              {featured.length === 0 && (
                <div className="rounded-lg border border-dashed p-6 text-center">
                  <p className="text-sm text-muted-foreground">
                    Nema istaknutih terapeuta. Dodajte terapeute klikom na dugme
                    ispod.
                  </p>
                </div>
              )}

              {featured.map((therapist, index) => (
                <div
                  key={therapist.id}
                  className="flex items-center gap-3 rounded-lg border bg-background p-3 transition-colors hover:bg-muted/30"
                >
                  {/* Position number */}
                  <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                    {index + 1}
                  </span>

                  {/* Reorder buttons */}
                  <div className="flex shrink-0 flex-col gap-0.5">
                    <button
                      type="button"
                      onClick={() => moveUp(index)}
                      disabled={index === 0}
                      className="rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-30"
                      aria-label="Pomjeri gore"
                    >
                      <CaretUp className="size-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveDown(index)}
                      disabled={index >= featured.length - 1}
                      className="rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-30"
                      aria-label="Pomjeri dolje"
                    >
                      <CaretDown className="size-4" />
                    </button>
                  </div>

                  {/* Avatar */}
                  {(therapist.profileImageUrl || therapist.userImage) ? (
                    <Img
                      src={(therapist.profileImageUrl || therapist.userImage)!}
                      alt={therapist.userName}
                      className="size-10 shrink-0 rounded-full object-cover"
                      sizes="40px"
                    />
                  ) : (
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                      {therapist.userName.charAt(0).toUpperCase()}
                    </div>
                  )}

                  {/* Name & specializations */}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">
                      {therapist.userName}
                    </p>
                    {parseSpecs(therapist.specializations).length > 0 && (
                      <p className="truncate text-xs text-muted-foreground">
                        {parseSpecs(therapist.specializations).join(", ")}
                      </p>
                    )}
                  </div>

                  {/* Remove button */}
                  <button
                    type="button"
                    onClick={() => removeTherapist(index)}
                    className="shrink-0 rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                    aria-label="Ukloni terapeuta"
                  >
                    <X className="size-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Add button */}
            {featured.length < 4 && (
              <Button
                type="button"
                variant="outline"
                className="mt-3"
                onClick={() => setPickerOpen(true)}
              >
                <Plus className="size-4" />
                Dodaj terapeuta
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Save bar */}
      <div
        className={cn(
          "sticky bottom-4 flex items-center justify-between rounded-xl border bg-card/95 p-4 shadow-lg backdrop-blur-sm transition-all sm:p-5",
          hasChanges ? "ring-2 ring-primary/20" : "ring-1 ring-border/50"
        )}
      >
        <div className="flex items-center gap-2 text-sm">
          {saveMutation.isSuccess && !hasChanges ? (
            <>
              <CheckCircle className="size-4 text-emerald-600" />
              <span className="font-medium text-emerald-600">
                Promjene spremljene
              </span>
            </>
          ) : hasChanges ? (
            <>
              <Warning className="size-4 text-amber-500" />
              <span className="text-muted-foreground">
                Imate nesačuvane promjene
              </span>
            </>
          ) : (
            <span className="text-muted-foreground">
              Sve postavke su ažurne
            </span>
          )}
        </div>
        <Button
          type="button"
          disabled={saveMutation.isPending || !hasChanges}
          onClick={handleSave}
        >
          {saveMutation.isPending ? (
            <CircleNotch className="size-4 animate-spin" />
          ) : (
            <FloppyDisk className="size-4" />
          )}
          {saveMutation.isPending ? "Spremanje..." : "Spremi promjene"}
        </Button>
      </div>

      {/* Therapist picker dialog */}
      <TherapistPickerDialog
        open={pickerOpen}
        onClose={() => {
          setPickerOpen(false);
          setSearchQuery("");
        }}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        featuredIds={therapistIds}
        isFull={therapistIds.length >= 4}
        onAdd={addTherapist}
      />
    </div>
  );
}

function TherapistPickerDialog({
  open,
  onClose,
  searchQuery,
  onSearchChange,
  featuredIds,
  isFull,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  featuredIds: string[];
  isFull: boolean;
  onAdd: (therapist: ApprovedTherapist) => void;
}) {
  const { data: therapists, isLoading } = useQuery({
    queryKey: ["admin", "approved-therapists"],
    queryFn: async () => {
      const res = await fetch("/api/therapists?status=approved");
      await throwIfNotOk(res, "Greška pri učitavanju terapeuta");
      return res.json() as Promise<ApprovedTherapist[]>;
    },
    enabled: open,
  });

  const filtered = useMemo(() => {
    if (!therapists) return [];
    if (!searchQuery.trim()) return therapists;
    const q = searchQuery.toLowerCase();
    return therapists.filter((t) => t.userName.toLowerCase().includes(q));
  }, [therapists, searchQuery]);

  const featuredSet = useMemo(() => new Set(featuredIds), [featuredIds]);

  return (
    <Dialog open={open} onClose={onClose} className="sm:max-w-lg">
      <DialogHeader onClose={onClose}>
        <AdminDialogHeading
          icon={UserPlus}
          title="Dodaj terapeuta"
          description="Odaberite terapeuta za prikaz na početnoj stranici."
        />
      </DialogHeader>

      <DialogBody className="flex flex-col gap-4">
        {/* Search input */}
        <div className="relative">
          <MagnifyingGlass className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Pretraži po imenu..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Therapist list */}
        <div className="space-y-1">
          {isLoading && (
            <div className="space-y-2 py-2">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 rounded-lg p-3"
                >
                  <div className="size-10 animate-pulse rounded-full bg-muted" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-4 w-32 animate-pulse rounded bg-muted" />
                    <div className="h-3 w-48 animate-pulse rounded bg-muted/60" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isLoading && filtered.length === 0 && (
            <div className="py-8 text-center">
              <p className="text-sm text-muted-foreground">
                {searchQuery
                  ? "Nema terapeuta koji odgovaraju pretrazi"
                  : "Nema dostupnih terapeuta"}
              </p>
            </div>
          )}

          {filtered.map((therapist) => {
            const isAlreadyFeatured = featuredSet.has(therapist.id);
            const isDisabled = isAlreadyFeatured || isFull;

            return (
              <button
                key={therapist.id}
                type="button"
                disabled={isDisabled}
                onClick={() => onAdd(therapist)}
                className={cn(
                  "flex w-full items-center gap-3 rounded-lg p-3 text-left transition-colors",
                  isDisabled
                    ? "cursor-not-allowed opacity-50"
                    : "hover:bg-muted/50"
                )}
              >
                {/* Avatar */}
                {(therapist.profileImageUrl || therapist.userImage) ? (
                  <Img
                    src={(therapist.profileImageUrl || therapist.userImage)!}
                    alt={therapist.userName}
                    className="size-10 shrink-0 rounded-full object-cover"
                    sizes="40px"
                  />
                ) : (
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                    {therapist.userName.charAt(0).toUpperCase()}
                  </div>
                )}

                {/* Info */}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">
                    {therapist.userName}
                  </p>
                  {parseSpecs(therapist.specializations).length > 0 && (
                    <p className="truncate text-xs text-muted-foreground">
                      {parseSpecs(therapist.specializations).join(", ")}
                    </p>
                  )}
                </div>

                {/* Status indicator */}
                {isAlreadyFeatured ? (
                  <span className="shrink-0 text-xs text-muted-foreground">
                    Već istaknuto
                  </span>
                ) : (
                  !isFull && (
                    <UserPlus className="size-4 shrink-0 text-muted-foreground" />
                  )
                )}
              </button>
            );
          })}
        </div>
      </DialogBody>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Zatvori
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
